import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FilterMatchMode, FilterService } from "primereact/api";
import { Chip } from "primereact/chip";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable, DataTableFilterMeta, DataTableStateEvent } from "primereact/datatable";
import { MultiSelect } from "primereact/multiselect";

import { SecurityGroupRoleAssignmentsReadModel, StringReferenceReadModel } from "../../../../../../@api";

import { compareValues } from "../../../../../../utils/functions/compareValues";
import { translationKeys } from "../../../../../models/app-translation-keys";
import { useStore } from "../../../../../stores/StoreContext";
import { TableSkeleton } from "../../../../Shared/LoadingSkeletons/TableSkeleton/TableSkeleton";
import EditSecurityGroupButton from "../EditSecurityGroupButton/EditSecurityGroupButton";
import { ISecurityGroupRoleAssignments } from "../PermissionsTab";

interface IPermissionChipsTableProps {
  securityGroups: ISecurityGroupRoleAssignments[];
  roles: StringReferenceReadModel[];
  isLoading: boolean;
  onReloadTable: () => void;
  globalFilterValue?: string;
  paginatorRows: number[];
  paginatorSelectedRows: number;
}

export const PermissionChipsTable: FunctionComponent<IPermissionChipsTableProps> = (
  props: IPermissionChipsTableProps,
) => {
  const { t } = useTranslation();
  const { userStore } = useStore();

  const [sortField, setSortField] = useState<string>("assignedRoleIdsSortField");
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(1);

  // Table data
  const [sortedData, setSortedData] = useState<ISecurityGroupRoleAssignments[]>([]);

  // Filters and sorting
  const [filters, setFilters] = useState<DataTableFilterMeta | undefined>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    assignedRoleIds: { value: null, matchMode: "RoleFilterMatchMode" as any },
  });

  useEffect(() => {
    const _filters = { ...filters };
    (_filters["global"] as any).value = String(props.globalFilterValue || "").trim();
    setFilters(_filters);
  }, [props.globalFilterValue]);

  FilterService.register(
    "RoleFilterMatchMode" as keyof ISecurityGroupRoleAssignments,
    (assignedRoleIds: string[], filterIds: StringReferenceReadModel[]) => {
      // Custom registered filter match mode since filterFunction prop on Column doesn't work as expected
      // GitHub issue and working example: https://github.com/primefaces/primereact/issues/3325

      if (!assignedRoleIds?.length || !filterIds?.length) {
        return true;
      }

      return assignedRoleIds.some((value: string) => filterIds.map(filterId => filterId.id).includes(value));
    },
  );

  useEffect(() => {
    if (!props.isLoading) {
      const sorted = dataTableSortFunction(sortField, sortOrder);
      setSortedData(sorted);
    }
  }, [props.isLoading, props.securityGroups, props.roles]);

  const dataTableSortFunction = (field: string, order: 1 | -1 | 0) => {
    const securityGroups = [...props.securityGroups];

    if (!order) {
      return securityGroups;
    }

    return securityGroups.sort((rowA: ISecurityGroupRoleAssignments, rowB: ISecurityGroupRoleAssignments) => {
      if (field === "assignedRoleIdsSortField") {
        const firstRoleA = getSortedRoles(rowA.assignedRoleIds)[0];
        const firstRoleB = getSortedRoles(rowB.assignedRoleIds)[0];
        return compareValues(firstRoleA?.displayName, firstRoleB?.displayName) * order;
      } else if (field === "securityGroup") {
        return compareValues(rowA.securityGroup.displayName, rowB.securityGroup.displayName) * order;
      } else {
        return compareValues((rowA as any)[field], (rowB as any)[field]) * order;
      }
    });
  };

  const getSortedRoles = (assignedRoleIds: string[]): StringReferenceReadModel[] => {
    return assignedRoleIds
      .map(roleId => props.roles.find(role => role.id === roleId))
      .filter(role => !!role)
      .sort((a, b) => a!.displayName.localeCompare(b!.displayName));
  };

  const AssignedRoleIdsBodyElement = (rowData: SecurityGroupRoleAssignmentsReadModel) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
      {rowData.assignedRoleIds
        .map(roleId => props.roles.find(role => role.id === roleId))
        .filter(role => !!role)
        .sort((a, b) => a!.displayName.localeCompare(b!.displayName))
        .map(role => (
          <Chip
            key={`${rowData.securityGroup.id}-${role.id}`}
            label={role.displayName}
            style={{ fontSize: "12px", whiteSpace: "nowrap" }}
          />
        ))}
    </div>
  );

  const AssignedRoleIdsFilterElement = (options: ColumnFilterElementTemplateOptions) => (
    <MultiSelect
      value={options.value ?? []}
      onChange={e => options.filterApplyCallback(e.value)}
      options={props.roles}
      optionLabel="displayName"
      placeholder={t(translationKeys.adminArea.appRoles.actions.filterRoles)}
      className="w-full md:w-20rem"
      maxSelectedLabels={2}
    />
  );

  const RoleAssignmentColumnAction = (rowData: SecurityGroupRoleAssignmentsReadModel) => (
    <EditSecurityGroupButton
      data={rowData}
      hide={false}
      roles={props.roles}
      onAddedSecurityGroup={props.onReloadTable}
    />
  );

  const onSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
    const sorted = dataTableSortFunction(event.sortField, event.sortOrder);
    setSortedData(sorted);
  };

  const tableTemplateMemo = useMemo(() => {
    if (props.isLoading) {
      return <TableSkeleton rows={2} columns={5} />;
    }

    return (
      <DataTable
        key={`${filters ? Object.keys(filters).length : 0}`}
        value={sortedData}
        filters={filters}
        globalFilterFields={["securityGroup.displayName", "assignedRolesForFilter"]}
        onFilter={(e: DataTableStateEvent) => setFilters(e.filters)}
        scrollable
        scrollHeight="25rem"
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder}
        paginator
        rows={props.paginatorSelectedRows}
        rowsPerPageOptions={props.paginatorRows}
        tableStyle={{ minWidth: "100%" }}
        dataKey={data => data.securityGroup.id}
        size="small"
        emptyMessage={t(translationKeys.adminArea.appRoles.noSecurityGroupsFound)}
      >
        <Column
          field="securityGroup"
          sortable
          header={t(translationKeys.adminArea.appRoles.securityGroup.displayName)}
          body={(rowData: SecurityGroupRoleAssignmentsReadModel) => rowData.securityGroup.displayName}
        />

        <Column
          field="assignedRoleIds"
          sortable
          filter
          showFilterMenu
          sortField="assignedRoleIdsSortField"
          showFilterMatchModes={false}
          filterElement={AssignedRoleIdsFilterElement}
          header={t(translationKeys.adminArea.appRoles.securityGroup.assignedRoles)}
          body={AssignedRoleIdsBodyElement}
        />

        <Column
          style={{ minWidth: "60px" }}
          headerStyle={{ minWidth: "60px" }}
          frozen
          alignFrozen="right"
          body={RoleAssignmentColumnAction}
        />
      </DataTable>
    );
  }, [sortedData, filters, props.isLoading, userStore.currentLanguage]);

  return tableTemplateMemo;
};
